

















































































import { Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import SwScrollViewScrollbar from './scroll-view-dropdown-scrollbar.vue';
import { SwExtendedVue } from '@sonicwall/unified-ui-framework/src/shared/sw-extended-vue';
import { domService } from '@sonicwall/unified-ui-framework/src/shared/dom-service';
import { CSSStyleOptions } from '@sonicwall/unified-ui-framework/src/shared/common-types';

const cssPrefix = 'sw-scroll-view';

/**
 * @event content-dom-updated
 * @event scroll
 */
@Component({
  components: {
    SwScrollViewScrollbar,
  },
})
export default class SwScrollView extends SwExtendedVue {
  @Prop({ type: Boolean, default: false })
  nativeScrollbar: boolean;

  @Prop({ type: String, default: 'light' })
  scrollbarTheme: 'light' | 'dark';

  @Prop({ type: Boolean, default: false })
  scrollbarAutoHide: boolean;

  @Prop({ type: Number, default: 1 })
  scrollbarZIndex: number;

  private cssPrefix = cssPrefix;

  public clientWidth: number = 0;
  public clientHeight: number = 0;
  public scrollWidth: number = 0;
  public scrollHeight: number = 0;
  public scrollTop: number = 0;
  public scrollLeft: number = 0;
  public lockScrollbar: boolean = false;
  private hideScrollbar: boolean = false;
  private contentObserver: MutationObserver | null = null;

  get viewStyle(): CSSStyleOptions {
    return {
      marginRight: `${-1 * domService.getScrollbarSize()}px`,
      marginBottom: `${-1 * domService.getScrollbarSize()}px`,
    };
  }
  get showVScrollbar(): boolean {
    return (this.scrollHeight > this.clientHeight) && (!this.effectiveHideScrollbar);
  }
  get showHScrollbar(): boolean {
    return (this.scrollWidth > this.clientWidth) && (!this.effectiveHideScrollbar);
  }
  get effectiveHideScrollbar(): boolean {
    return (!this.lockScrollbar && this.hideScrollbar);
  }

  public syncSizes(): void {
    const viewContEl: HTMLElement | undefined = this.$refs.viewCont as HTMLElement;
    if (!viewContEl) { return; }
    this.clientWidth = viewContEl.clientWidth;
    this.clientHeight = viewContEl.clientHeight;
    this.scrollWidth = viewContEl.scrollWidth;
    this.scrollHeight = viewContEl.scrollHeight;
    this.scrollTop = viewContEl.scrollTop;
    this.scrollLeft = viewContEl.scrollLeft;
  }

  private updateSizes(): void {
    if (!this.$refs.viewCont) { return; }
    this.syncSizes();
    this.updateScrollbarPercentage();

    /** whenever we want to update the sizes, the scrollbars have to be shown so that they can
     * pick up these updated values to update the sizes of themselves.
     */
    if (this.scrollbarAutoHide && this.hideScrollbar) {
      this.hideScrollbar = false;
      this.$nextTick(() => {
        this.hideScrollbar = true;
      });
    }
  }

  private updateScrollbarPercentage(): void {
    if (!this.$refs.vScroll && !this.$refs.hScroll) { return; }
    const { scrollTop, scrollLeft } = (this.$refs.viewCont as HTMLElement);

    if (this.$refs.vScroll && this.showVScrollbar) {
      (this.$refs.vScroll as SwScrollViewScrollbar).updatePosition(scrollTop / (this.scrollHeight - this.clientHeight));
    }
    if (this.$refs.hScroll && this.showHScrollbar) {
      (this.$refs.hScroll as SwScrollViewScrollbar).updatePosition(scrollLeft / (this.scrollWidth - this.clientWidth));
    }
  }

  public updatePercentagePosition(percentagePos: number, forVertical: boolean) {
    if (!this.$refs.viewCont) { return; }
    const viewContEl = this.$refs.viewCont as HTMLElement;
    if (forVertical) {
      viewContEl.scrollTop = percentagePos * (viewContEl.scrollHeight - viewContEl.clientHeight);
    } else {
      viewContEl.scrollLeft = percentagePos * (viewContEl.scrollWidth - viewContEl.clientWidth);
    }
  }

  /** @public imperatively set top/left */
  public updatePosition(pos: number, forVertical: boolean) {
    if (!this.$refs.viewCont) { return; }
    const viewContEl = this.$refs.viewCont as HTMLElement;
    if (forVertical) {
      viewContEl.scrollTop = pos;
      this.scrollTop = viewContEl.scrollTop;
    } else {
      viewContEl.scrollLeft = pos;
      this.scrollLeft = viewContEl.scrollLeft;
    }
  }

  private onViewScroll(event: Event) {
    this.syncSizes();
    this.updateScrollbarPercentage();
    this.$emit('scroll', event);
  }

  private onMouseEnterView() {
    this.hideScrollbar = false;
  }

  private onMouseLeaveView() {
    this.hideScrollbar = this.scrollbarAutoHide;
  }

  private onContentDomUpdated() {
    this.updateSizes();
    this.$emit('content-dom-updated');
  }

  private mounted() {
    this.$nextTick(() => {
      this.updateSizes();
      (this.$refs.viewCont as HTMLElement) && (this.$refs.viewCont as HTMLElement).addEventListener('scroll', this.onViewScroll);
      domService.resizeObserver.on(this.$refs.slotWrapper as HTMLElement, this.updateSizes);
      domService.resizeObserver.on(this.$el as HTMLElement, this.updateSizes);
      domService.visibilityObserver.on(this.$el as HTMLElement, this.updateSizes);
      /** Since it is hard to make a generic sensor for content width, we are using MutationObserver
       * as an alternative way to monitor the size change of contained content.
       */
      this.contentObserver = domService.mutationObserverManager.setup(
        this.$refs.slotWrapper as HTMLElement,
        { childList: true, subtree: true, attributes: true, characterData: true },
        _.throttle(this.onContentDomUpdated, 200),
      );

      if (this.scrollbarAutoHide) {
        this.$nextTick(() => {
          this.hideScrollbar = true;
          this.$el.addEventListener('mouseenter', this.onMouseEnterView);
          this.$el.addEventListener('mouseleave', this.onMouseLeaveView);
        });
      }
    });
  }

  private beforeDestroy() {
    if (this.$refs.viewCont) {
      (this.$refs.viewCont as HTMLElement).removeEventListener('scroll', this.onViewScroll);
    }
    if (this.$el) {
      this.$el.removeEventListener('mouseenter', this.onMouseEnterView);
      this.$el.removeEventListener('mouseleave', this.onMouseLeaveView);
      domService.resizeObserver.off(this.$el as HTMLElement, this.updateSizes);
      domService.visibilityObserver.off(this.$el as HTMLElement);
    }
    if (this.$refs.slotWrapper) {
      domService.resizeObserver.off(this.$refs.slotWrapper as HTMLElement, this.updateSizes);
    }
    if (this.contentObserver) {
      this.contentObserver.disconnect();
    }
  }
}
